import { useState } from 'react'
import { useGameState } from '@/hooks/useGameState'
import { bestScore, summarize, todayUTC } from '@/lib/history'
import { percentOfTop, placing, shareResults, shareText, type ShareOutcome } from '@/lib/share'
import { cutoffs, distribution, standing, type CommunityStats } from '@/lib/stats'

export function Results({ stats, onClose }: { stats: CommunityStats | null; onClose: () => void }) {
  const { puzzle, record, setView } = useGameState()
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null)
  if (!puzzle || !record) return null

  const isToday = puzzle.date === todayUTC()
  const mine = bestScore(record)
  const top = puzzle.best_play?.score ?? 0
  const pct = percentOfTop(mine, top)
  const place = placing(puzzle, mine)
  const personal = summarize(todayUTC())
  const text = shareText(puzzle, mine, stats)

  const share = async () => setOutcome(await shareResults(text))

  return (
    <div className="fixed inset-0 z-10 flex items-end justify-center bg-slate-950/50 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="results-title">
      <div className="max-h-full w-full max-w-sm space-y-4 overflow-y-auto panel p-5">
        <div className="flex items-start justify-between">
          <h2 id="results-title" className="display text-xl">
            {mine >= top ? 'Perfect play!' : isToday ? 'Done for today' : 'Puzzle complete'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="-m-1 p-1 text-2xl leading-none muted hover:opacity-70">
            ×
          </button>
        </div>

        <div className="text-center">
          <div className="display text-5xl tabular-nums">{mine}</div>
          <div className="text-sm muted">
            of {top} possible ({pct}%)
          </div>
          {place && (
            <div className="mt-1 text-sm font-semibold">
              #{place.rank.toLocaleString('en')} of {place.total.toLocaleString('en')} possible plays
            </div>
          )}
        </div>

        {puzzle.best_play && (
          <div className="callout flex items-center justify-between gap-3 p-3 text-sm">
            <span>
              Top play: <strong>{puzzle.best_play.words.join(' + ')}</strong> for {top} points
            </span>
            {mine < top && (
              <button
                type="button"
                className="shrink-0 font-semibold underline underline-offset-2"
                onClick={() => {
                  setView('top')
                  onClose()
                }}
              >
                Show me
              </button>
            )}
          </div>
        )}

        {stats && (
          <section aria-labelledby="everyone-title" className="space-y-2">
            <h3 id="everyone-title" className="sr-only">
              How everyone did
            </h3>
            <p className="text-center text-sm">
              <span className="accent-text font-semibold">{standing(stats, mine)}</span>
            </p>
            <Breakdown stats={stats} mine={mine} />
          </section>
        )}

        <dl className="grid grid-cols-3 gap-2 text-center text-sm">
          <Item label="Players" value={stats?.submissions ?? '—'} />
          <Item label="Median" value={stats?.median_score ?? '—'} />
          <Item label="High" value={stats?.high_score ?? '—'} />
          <Item label="Played" value={personal.played} />
          <Item label="Streak" value={personal.currentStreak} />
          <Item label="Max streak" value={personal.maxStreak} />
        </dl>

        <button type="button" onClick={share} className="btn-primary w-full">
          {outcome === 'copied' ? 'Copied! Paste it anywhere' : 'Share'}
        </button>
        {outcome === 'failed' && (
          // Neither sharing nor copying worked: let the player copy it by hand.
          <div className="space-y-1">
            <label htmlFor="share-text" className="block text-center text-xs muted">
              Couldn&apos;t copy automatically. Select the text and copy it:
            </label>
            <textarea
              id="share-text"
              readOnly
              value={text}
              rows={text.split('\n').length}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full resize-none rounded-md border border-current/20 bg-transparent p-2 text-sm"
            />
          </div>
        )}
        <p className="text-center text-xs muted">Sharing shows your percentage and place, not your score.</p>
        <p className="text-center text-xs muted">
          {isToday
            ? 'A new puzzle arrives at midnight UTC.'
            : "Your score is on this day's scoreboard. Archive plays don't count toward streaks."}
        </p>
      </div>
    </div>
  )
}

/** The day's scores as a small bar chart with the player's band picked out, and the score each top band needed. */
function Breakdown({ stats, mine }: { stats: CommunityStats; mine: number }) {
  const bars = cutoffs(stats).length ? distribution(stats, mine) : []
  if (!bars.length) return null
  const most = Math.max(...bars.map((b) => b.count))
  const range = (b: { from: number; to: number }) => (b.from === b.to ? `${b.from}` : `${b.from}–${b.to}`)
  return (
    <>
      <figure>
        <ul className="muted flex h-16 items-end gap-1" aria-label="Scores of everyone who played">
          {bars.map((b) => (
            <li key={b.from} className={b.mine ? 'dist-bar dist-bar-mine' : 'dist-bar'} style={{ height: `${Math.max(4, (b.count / most) * 100)}%` }}>
              <span className="sr-only">
                {range(b)} points: {b.count} {b.count === 1 ? 'player' : 'players'}
                {b.mine && ', including you'}
              </span>
            </li>
          ))}
        </ul>
        <figcaption className="muted mt-1 flex justify-between text-xs tabular-nums">
          <span>{bars[0].from}</span>
          <span>Your score is highlighted</span>
          <span>{bars.at(-1)!.to}</span>
        </figcaption>
      </figure>
      <dl className="flex justify-around gap-1 text-center text-xs" aria-label="Score needed">
        {cutoffs(stats).map((c) => (
          <div key={c.percent}>
            <dd className="text-sm font-bold tabular-nums">{c.score}+</dd>
            <dt className="muted">Top {c.percent}%</dt>
          </div>
        ))}
      </dl>
    </>
  )
}

function Item({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <dd className="text-lg font-bold tabular-nums">{value}</dd>
      <dt className="text-xs muted">{label}</dt>
    </div>
  )
}
